import { Account } from '../../Account/accountApi'

export const canManageSalesLeads = (currentUser: Account | undefined): boolean => {
  return currentUser?.roles?.includes('Administrator') ?? false
}

export type SalesLead = {
  id: number
  name: string
}

export type ViewSalesLead = SalesLead & {
  isDeleted: boolean
}

export type SalesLeadsState = {
  addedSalesLeads: SalesLead[]
  updatedSalesLeads: SalesLead[]
  deletedSalesLeadsIds: number[]
}

export const defaultSalesLead = (): SalesLead => ({
  id: 0,
  name: '',
})

export const defaultSalesLeadsState = (): SalesLeadsState => ({
  addedSalesLeads: [],
  updatedSalesLeads: [],
  deletedSalesLeadsIds: []
})

export const hasChanges = (state: SalesLeadsState): boolean => {
  return state.addedSalesLeads.length > 0 ||
    state.updatedSalesLeads.length > 0 ||
    state.deletedSalesLeadsIds.length > 0
}

export const updateSalesLead = (state: SalesLeadsState, serverSalesLeads: SalesLead[], salesLeadId: number, update: (salesLead: SalesLead) => SalesLead): SalesLeadsState => {
  if (state.addedSalesLeads.some(x => x.id === salesLeadId)) {
    return {
      ...state,
      addedSalesLeads: state.addedSalesLeads.map(x => x.id === salesLeadId ? update(x) : x)
    }
  }

  if (state.updatedSalesLeads.some(x => x.id === salesLeadId)) {
    return {
      ...state,
      updatedSalesLeads: state.updatedSalesLeads.map(x => x.id === salesLeadId ? update(x) : x)
    }
  }

  const serverSalesLead = serverSalesLeads.find(x => x.id === salesLeadId)
  if (serverSalesLead) {
    return {
      ...state,
      updatedSalesLeads: [
        ...state.updatedSalesLeads,
        update(serverSalesLead)
      ]
    }
  }

  return state
}

export const addSalesLead = (state: SalesLeadsState, name: string | null | undefined): SalesLeadsState => {
  if (!name) {
    return state
  }
  return {
    ...state,
    addedSalesLeads: [...state.addedSalesLeads, { ...defaultSalesLead(), name, id: -state.addedSalesLeads.length }]
  }
}

const getUpdatedSalesLead = (state: SalesLeadsState, serverSalesLead: SalesLead): SalesLead => {
  const updated = state.updatedSalesLeads.find(x => x.id === serverSalesLead.id)
  return updated ?? serverSalesLead
}

const getAllUpdatedSalesLeads = (state: SalesLeadsState, serverSalesLead: SalesLead[]) => {
  return serverSalesLead.map(x => getUpdatedSalesLead(state, x)).concat(state.addedSalesLeads)
}

export const createViewSalesLead = (state: SalesLeadsState, serverSalesLead: SalesLead[]): ViewSalesLead[] => {
  const rows = getAllUpdatedSalesLeads(state, serverSalesLead).map(x => {
    const isDeleted = state.deletedSalesLeadsIds.includes(x.id)
    return {
      ...x,
      isDeleted
    }
  })

  return rows.sort((a, b) => a.name.localeCompare(b.name))
}

export const deleteSalesLead = (state: SalesLeadsState, salesLeadId: number): SalesLeadsState => {
  if (state.deletedSalesLeadsIds.some(x => x === salesLeadId)) {
    return state
  }
  return {
    ...state,
    deletedSalesLeadsIds: [...state.deletedSalesLeadsIds, salesLeadId]
  }
}

export const undeleteSalesLead = (state: SalesLeadsState, salesLeadId: number): SalesLeadsState => {
  return { ...state, deletedSalesLeadsIds: state.deletedSalesLeadsIds.filter(x => x !== salesLeadId) }
}

export const buildStateForSave = (state: SalesLeadsState): SalesLeadsState => {
  return {
    ...state,
    addedSalesLeads: state.addedSalesLeads.filter(x => !state.deletedSalesLeadsIds.includes(x.id)),
    updatedSalesLeads: state.updatedSalesLeads.filter(x => !state.deletedSalesLeadsIds.includes(x.id)),
  }
}

export const onSave = (state: SalesLeadsState): SalesLeadsState => {
  return buildStateForSave(state)
}
